// ShortsView.tsx — "Shorts" tab of /content-map.
//
// A catalog of the MyJarvis AI Shorts: vertical (9:16) videos built in-house
// from YouTube videos Erez actually watched. Each one distils a single insight
// into a ~45s branded vertical clip — own narration (Kokoro), own Remotion
// render, zero creator footage, zero copyright risk.
//
// Source pipeline lives at ~/Workspace/jarvis-pai/agents/ben/mj389-shorts/
// (one command per short). Videos are hosted on the dashboard R2 bucket.
//
// This is a presentational catalog — the list below is curated. When the set
// grows we can move it behind /api, but a small typed array keeps the explicit
// ask (a browsable shelf of the shorts) shippable today.

import { useRef, useState } from "react";
import { Film, Play } from "lucide-react";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type ShortItem = {
  id: string;
  title: string; // the on-screen hook
  kicker: string; // the one-line takeaway
  source: string; // the watched video / author it came from
  durationLabel: string;
  videoUrl: string;
  badge?: string; // optional corner tag, e.g. the production style
};

const SHORTS: ShortItem[] = [
  {
    id: "01-folder-beats-vectordb",
    title: "Everyone is building AI memory wrong.",
    kicker: "A folder beats the vector database.",
    source: "Google OKF · Karpathy's LLM-Wiki",
    durationLabel: "0:27",
    badge: "v2 · animated",
    videoUrl:
      "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/shorts/35f2d4aa-4d53-44de-a0b7-eeda209ce9e4-01-folder-beats-vectordb-v2.mp4",
  },
  {
    id: "02-vending-fbi",
    title: "We let an AI run a shop. It called the FBI.",
    kicker: "Judge your agents in dollars.",
    source: "Andon Labs · VendingBench",
    durationLabel: "0:22",
    badge: "v2 · animated",
    videoUrl:
      "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/shorts/962b3471-7626-4b6a-9d1e-b097424769bd-02-vending-fbi-v2.mp4",
  },
  {
    id: "03-five-moats",
    title: "Most AI apps are already dead.",
    kicker: "Stand on a moat, not a wrapper.",
    source: "AI Moats · 5 Safe Places to Build",
    durationLabel: "0:25",
    badge: "v2 · animated",
    videoUrl:
      "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/shorts/a6a81ac0-ba71-44ed-8462-554755adf39c-03-five-moats-v2.mp4",
  },
];

const FACE = '"Inter Tight","Inter",system-ui,-apple-system,sans-serif';

function ShortCard({ item }: { item: ShortItem }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play();
    setPlaying(true);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
      }}
    >
      {/* 9:16 video frame */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "9 / 16",
          background: "#1C1B18",
        }}
      >
        <video
          ref={videoRef}
          src={item.videoUrl}
          controls={playing}
          playsInline
          preload="metadata"
          onEnded={() => setPlaying(false)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {item.badge && (
          <span
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              fontFamily: FACE,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: "#1C1B18",
              background: "#E7B96B",
              padding: "3px 9px",
              borderRadius: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}
          >
            {item.badge}
          </span>
        )}
        {!playing && (
          <button
            onClick={play}
            aria-label={`Play ${item.title}`}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)",
              border: "none",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.92)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.30)",
              }}
            >
              <Play
                style={{ width: 26, height: 26, marginLeft: 3, color: "#1C1B18" }}
                fill="#1C1B18"
              />
            </span>
            <span
              style={{
                position: "absolute",
                bottom: 12,
                right: 12,
                fontFamily: FACE,
                fontSize: 12,
                fontWeight: 700,
                color: T.white,
                background: "rgba(0,0,0,0.55)",
                padding: "3px 8px",
                borderRadius: 6,
                letterSpacing: "0.02em",
              }}
            >
              {item.durationLabel}
            </span>
          </button>
        )}
      </div>

      {/* meta */}
      <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontFamily: FACE,
            fontSize: 15,
            fontWeight: 800,
            lineHeight: 1.25,
            color: T.ink,
            letterSpacing: "-0.01em",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            fontFamily: FACE,
            fontSize: 12,
            fontWeight: 700,
            color: T.amber,
            background: T.amberSoft,
            border: `1px solid ${T.amber}`,
            borderRadius: 999,
            padding: "3px 10px",
          }}
        >
          {item.kicker}
        </div>
        <div
          style={{
            fontFamily: FACE,
            fontSize: 12,
            color: T.ink3,
            marginTop: 2,
          }}
        >
          From {item.source}
        </div>
      </div>
    </div>
  );
}

export function ShortsView() {
  return (
    <div>
      {/* header */}
      <div style={{ marginBottom: 24, maxWidth: 760 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FACE,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: T.amber,
            marginBottom: 10,
          }}
        >
          <Film style={{ width: 14, height: 14 }} />
          AI Shorts
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: FACE,
            fontSize: 26,
            fontWeight: 800,
            color: T.ink,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          One insight, one vertical clip.
        </h2>
        <p
          style={{
            margin: "10px 0 0",
            fontFamily: FACE,
            fontSize: 14.5,
            lineHeight: 1.6,
            color: T.ink2,
          }}
        >
          Built in-house from YouTube videos worth watching — each short distils a
          single idea into a ~45-second branded vertical. Own narration, own
          render, no creator footage, zero copyright risk.
        </p>
      </div>

      {/* grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(232px, 1fr))",
          gap: 22,
        }}
      >
        {SHORTS.map((s) => (
          <ShortCard key={s.id} item={s} />
        ))}
      </div>
    </div>
  );
}
